import { PublicationError } from '@streamlain/doc-publication-contract';

import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';
import { requireLoadedShardDirectory } from './data-source-shard-directory.js';
import {
  flushLocalRowPublications,
  hasPendingRowPublications,
  heldRowShardDocIds,
  onRowPublicationRejectionReported,
} from './data-source-shard-writing.js';
import { getRowShardWriteBarriers } from './data-source-values.js';

/** Window owners are local resource references, never persisted shard metadata. */
export function setWindowShardDocuments(
  context: DatabaseSourceRuntime,
  owner: string,
  docIds: ReadonlySet<string>
): void {
  const previous = context._shardedWindowOwners.get(owner) ?? new Set<string>();
  context._shardedWindowOwners.set(owner, new Set(docIds));
  const retained = new Set(context._pinnedShardedDocs);
  for (const docs of context._shardedWindowOwners.values())
    for (const id of docs) retained.add(id);
  for (const id of context._activeShardedWindowDocs) {
    // Only release this window’s prior references; readiness owns independent loads.
    if (!previous.has(id) || retained.has(id)) {
      retained.add(id);
      continue;
    }
    for (const dispose of context._rowShardDocLoadDisposers.get(id) ?? [])
      dispose();
    context._rowShardDocLoadDisposers.delete(id);
    context._shardedDocObserverDisposers.get(id)?.();
    releaseShardCaches(context, id);
  }
  context._shardedRowIds = context._shardedRowIds.filter(id =>
    context._shardedRowsById.has(id)
  );
  context._activeShardedWindowDocs = retained;
}
export function releaseWindowShardDocuments(
  context: DatabaseSourceRuntime,
  owner: string
): void {
  // Readiness can load a shard before a projection owns a row window.
  // Releasing an absent owner must not prune that unrelated load and cache.
  if (!context._shardedWindowOwners.has(owner)) return;
  setWindowShardDocuments(context, owner, new Set());
  context._shardedWindowOwners.delete(owner);
}

/** Release derived caches after the caller released its own load reference. */
function releaseShardCaches(context: DatabaseSourceRuntime, id: string): void {
  context._shardedDocObserverDisposers.delete(id);
  context._shardedObservedDocs.delete(id);
  context._shardedReadyDisposers.get(id)?.();
  context._shardedReadyDisposers.delete(id);
  context._shardedReadyObservedDocs.delete(id);
  context._missingRowShardDocIds.delete(id);
  context._publicationViewDisposers.get(id)?.();
  context._publicationViewDisposers.delete(id);
  context._publicationViewStates.delete(id);
  for (const row of context._shardedRowsByDocId.get(id)?.rows ?? []) {
    context._shardedRowsById.delete(row.id);
    context._shardedSearchTextByRowId.delete(row.id);
  }
  context._shardedRowsByDocId.delete(id);
}

/** Pending write references are released only after the existing sync barrier. */
const writeLeases = new WeakMap<
  DatabaseSourceRuntime,
  {
    pending: Map<string, number>;
    version: number;
    scheduled: boolean;
    /** A retain or report came while a release ran; it runs once more. */
    rerun: boolean;
    unsubscribe: () => void;
    running?: Promise<void>;
    abort?: AbortController;
  }
>();
const writeOwner = 'pending-database-writes';
const tailOwner = 'canonical-append-tail';

export function retainWriteShardDocument(
  context: DatabaseSourceRuntime,
  docId: string
): void {
  let lease = writeLeases.get(context);
  if (!lease) {
    lease = {
      pending: new Map(),
      version: 0,
      scheduled: false,
      rerun: false,
      unsubscribe: onRowPublicationRejectionReported(context, () =>
        scheduleRowShardRelease(context)
      ),
    };
    writeLeases.set(context, lease);
  }
  lease.pending.set(docId, ++lease.version);
  context._shardedWindowOwners.set(writeOwner, new Set(lease.pending.keys()));
  context._activeShardedWindowDocs.add(docId);
  scheduleRowShardRelease(context);
}

function scheduleRowShardRelease(context: DatabaseSourceRuntime): void {
  const lease = writeLeases.get(context);
  if (!lease || context._disposed) return;
  if (lease.running) {
    lease.rerun = true;
    return;
  }
  if (lease.scheduled) return;
  lease.scheduled = true;
  queueMicrotask(() => {
    lease.scheduled = false;
    // Synchronous editor writes remain local-first. A failed/offline barrier
    // retains their documents; the awaited consumer gets the same rejection.
    // The release only waits for the local publications: taking them here
    // would drop a rejection before its writer or a flush could report it.
    if (!context._disposed) void releaseRowShardWrites(context).catch(() => {});
  });
}

/** A shard this source still writes into exists locally before the server holds it. */
export function hasPendingWriteShardDocument(
  context: DatabaseSourceRuntime,
  docId: string
): boolean {
  return writeLeases.get(context)?.pending.has(docId) ?? false;
}

function releaseConfirmedWriteDocuments(
  context: DatabaseSourceRuntime,
  confirmed: ReadonlySet<string>
): void {
  const lease = writeLeases.get(context);
  if (lease?.pending.size)
    context._shardedWindowOwners.set(writeOwner, new Set(lease.pending.keys()));
  else context._shardedWindowOwners.delete(writeOwner);
  // Keep one already-hydrated append tail, not every previously written shard.
  // Synchronous editor/import appends can reuse it without a rehydration gap.
  const previousTail =
    context._shardedWindowOwners.get(tailOwner) ?? new Set<string>();
  // Without its last directory part loaded the tail is not known yet, so
  // none is retained until the part arrives.
  const directory = requireLoadedShardDirectory(
    context._model,
    context.databaseId
  );
  const manifest = directory.missingParts.length
    ? undefined
    : directory.manifest;
  const tail = context.isFileIndexDatabase()
    ? manifest?.shards.find(
        shard => shard.shardId === context._fileIndexWriterShardId
      )
    : manifest?.shards.at(-1);
  const nextTail =
    tail &&
    tail.rowCount > 0 &&
    context._rowShardDocLoadDisposers.has(tail.serverDocId)
      ? new Set([tail.serverDocId])
      : new Set<string>();
  if (nextTail.size) context._shardedWindowOwners.set(tailOwner, nextTail);
  else context._shardedWindowOwners.delete(tailOwner);
  for (const id of new Set([...confirmed, ...previousTail])) {
    if (
      context._pinnedShardedDocs.has(id) ||
      [...context._shardedWindowOwners.values()].some(docs => docs.has(id))
    )
      continue;
    // Prune only this write owner's confirmed references. Readiness, window,
    // relation and Undo references are independent of the pending write lease.
    for (const dispose of context._rowShardDocLoadDisposers.get(id) ?? [])
      dispose();
    context._rowShardDocLoadDisposers.delete(id);
    context._shardedDocObserverDisposers.get(id)?.();
    releaseShardCaches(context, id);
    context._activeShardedWindowDocs.delete(id);
  }
  context._shardedRowIds = context._shardedRowIds.filter(id =>
    context._shardedRowsById.has(id)
  );
}

export async function flushRowShardWrites(
  context: DatabaseSourceRuntime,
  signal?: AbortSignal
): Promise<void> {
  const localPublicationFlush = flushLocalRowPublications(context);
  if (localPublicationFlush) await localPublicationFlush;
  await releaseRowShardWrites(context, signal);
}

/**
 * Releases the retained write documents after the sync barrier. Each round
 * waits for the local publications and keeps the documents of a rejection
 * nobody reported yet; every confirmed document is freed.
 */
async function releaseRowShardWrites(
  context: DatabaseSourceRuntime,
  signal?: AbortSignal
): Promise<void> {
  const lease = writeLeases.get(context);
  if (!lease?.pending.size) return;
  if (signal?.aborted || context._disposed)
    throw new PublicationError('CANCELLED');
  const abort = () => lease.abort?.abort();
  signal?.addEventListener('abort', abort, { once: true });
  try {
    lease.running ??= (async () => {
      for (;;) {
        // Only waits when local publications are still on their way.
        const held = hasPendingRowPublications(context)
          ? await heldRowShardDocIds(context)
          : new Set<string>();
        const submitted = new Map(
          [...lease.pending].filter(([docId]) => !held.has(docId))
        );
        if (!submitted.size) return;
        if (context._disposed) throw new PublicationError('CANCELLED');
        const barriers = getRowShardWriteBarriers(context._hostModel.store);
        if (!barriers?.size)
          throw new Error('Database write sync barrier is unavailable.');
        lease.abort = new AbortController();
        await Promise.all(
          [...barriers].map(barrier =>
            barrier({
              blockId: context.blockId,
              databaseId: context.databaseId,
              rootDocId: context.storageDoc.id,
              workspaceId: context._model.store.workspace.id,
              rowShardDocIds: [...submitted.keys()],
              signal: lease.abort!.signal,
            })
          )
        );
        if (lease.abort.signal.aborted || context._disposed)
          throw new PublicationError('CANCELLED');
        const confirmed = new Set<string>();
        for (const [id, version] of submitted) {
          if (lease.pending.get(id) !== version) continue;
          lease.pending.delete(id);
          confirmed.add(id);
        }
        releaseConfirmedWriteDocuments(context, confirmed);
      }
    })().finally(() => {
      lease.running = undefined;
      lease.abort = undefined;
      if (!lease.rerun) return;
      lease.rerun = false;
      scheduleRowShardRelease(context);
    });
    await lease.running;
  } finally {
    signal?.removeEventListener('abort', abort);
  }
}

export function disposeRowShardWrites(context: DatabaseSourceRuntime): void {
  const lease = writeLeases.get(context);
  lease?.abort?.abort();
  lease?.unsubscribe();
  writeLeases.delete(context);
}
